import { useCallback, useEffect, useRef } from "react"
import { useSplitScreenStore } from "store/splitScreen.store"
import AnnotationService from "services/annotations.service"
import { smartNavDebug } from "../smartNavDebug"
import { preservePaneViewport } from "../core/viewerPanes"
import { preservePaneToolModes } from "../core/viewerTools"
import { getSelectionEntityId, resolveSmartNavigationPlan } from "./smartNavigation.helpers"
import type { MutableRefObject } from "react"
import type { WebViewerInstance } from "@pdftron/webviewer"
import type { TEcall, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"
import type { TViewerPane } from "services/annotations.service"
import type { TSmartNavIndexMemory, TSmartNavPlan, TSmartNavSelection } from "./smartNavigation.helpers"
import type { TJumpSession, TJumpSessionOptions } from "./useJumpNavigation"

// SMART NAVIGATION between the two split-screen panes — #1311, part 2.
//
// The rule (design + Notion spec): the pane a selection was made in becomes the PRIMARY view and
// keeps showing what was selected; the other pane becomes SECONDARY, auto-displays the connected
// COUNTERPART entity (eref → its ecalls, ecall → its erefs) and hosts a jumping-navigation session
// over that set. Selecting in the Secondary pane flips the roles and recomputes everything.
//
// This hook is the machine's wiring only — the case table itself is the pure
// `resolveSmartNavigationPlan` in ./smartNavigation.helpers, and the session, its bar and its
// prev/next belong to `useJumpNavigation` (a smart-nav session IS a jump session, just one whose
// items land in a pane other than the one its source lives in). What is left here is: the gate, the
// Primary-pane publication, the per-pane index memory, and the re-entrancy guard.
//
// Three invariants worth keeping:
//
//   • THE GATE IS TOTAL (decision D4). With "Auto open floor plan" off, or with split screen closed,
//     this hook does nothing beyond publishing which pane was last clicked — the editor behaves
//     exactly as it did before #1311.
//   • PRIMARY FOLLOWS EVERY SELECTION, gate or no gate. It is what the active-pane border marks, and
//     the design puts that border on a pane with a plain furniture selected too — so the pane is
//     published for any entity, while only eref/ecall selections produce a session.
//   • APPLYING A PLAN MUST NOT RE-ENTER. Selecting the counterpart goes through the orchestrator's
//     own click handlers, which notify this hook right back; `isApplyingRef` is what stops the two
//     panes from handing the Primary role to each other forever.
//   • ONLY THE SECONDARY PANE MOVES (D12 / AC18). Nothing here scrolls the Primary pane — but
//     applying a plan opens the Secondary pane's sidebar, which narrows BOTH panes, and because the
//     panes sit in a fit-zoom mode Apryse then re-fits the Primary one too (measured: page 14 at 22%
//     became page 27 at 12%). `preservePaneViewport` is what puts it back; see its own comment.

// How long to wait for the panel-driven re-layout before the final selection restore. Same
// animation preservePaneViewport / preservePaneToolModes wait out, so the same figure.
const SELECTION_RESTORE_SETTLE_MS = 350

type TSmartNavigationCtx = {
	isModalOpen: boolean
	// Needed to resolve the Primary pane's viewport and both panes' tool modes for the
	// preserve-and-restore passes above.
	instanceRef: MutableRefObject<WebViewerInstance | null>
	// Re-selecting after Apryse's tool reset must not look like a NEW user selection, or this hook
	// would flip the Primary role on its own. See the orchestrator's selectInPaneSilently.
	selectInPaneSilently: (pane: TViewerPane, select: () => void) => void
	// Every ecall in the quotation, read at notify time (the eref → ecalls direction).
	allEcallsRef: MutableRefObject<TEcall[]>
	findErefWithFurnitureById: (erefId: number) => TErefWithFurniture | undefined
	// The jump session this hook drives (see the note above).
	jumpSession: TJumpSession | null
	jumpSessionRef: MutableRefObject<TJumpSession | null>
	closeJumpSession: () => void
	startEcallJumpSession: (
		source: { kind: "eref"; eref: TErefWithFurniture } | { kind: "furniture"; furniture: TFurniture },
		items: TEcall[],
		startIndex: number,
		options?: TJumpSessionOptions,
	) => void
	startErefJumpSession: (
		source: { kind: "ecall"; ecall: TEcall },
		items: TErefWithFurniture[],
		startIndex: number,
		options?: TJumpSessionOptions,
	) => void
	// Where the orchestrator's click handlers publish their selections. Assigned on every render so
	// the handlers — which are declared long before this hook runs and must stay referentially
	// stable — always reach the current closure. `hasOwnScroll` is true when the caller already
	// issued its own scrollToEref/scrollToEcall/scrollToFurniture for THIS pane (a fresh click with
	// `scroll: true`) — see notifySelection for why that pane must not also be pinned/restored.
	notifyRef: MutableRefObject<
		((selection: TSmartNavSelection | null, pane: TViewerPane, hasOwnScroll: boolean) => void) | null
	>
}

export const useSmartNavigation = ({
	isModalOpen,
	instanceRef,
	selectInPaneSilently,
	allEcallsRef,
	findErefWithFurnitureById,
	jumpSession,
	jumpSessionRef,
	closeJumpSession,
	startEcallJumpSession,
	startErefJumpSession,
	notifyRef,
}: TSmartNavigationCtx) => {
	const isSplitActive = useSplitScreenStore((state) => state.isSplitActive)
	const isAutoOpenFloorPlan = useSplitScreenStore((state) => state.isAutoOpenFloorPlan)
	const primaryPane = useSplitScreenStore((state) => state.primaryPane)
	const setPrimaryPane = useSplitScreenStore((state) => state.setPrimaryPane)
	const setAutoOpenFloorPlan = useSplitScreenStore((state) => state.setAutoOpenFloorPlan)

	// Remembered session positions per (pane, entity) — decision D8, "reuse of previous eCall
	// navigation". A ref, not state: it is written from inside the apply path and never rendered.
	const indexMemoryRef = useRef<TSmartNavIndexMemory>({})
	const isApplyingRef = useRef(false)
	// Bumped on every apply so a deferred selection restore belonging to an older one bails instead
	// of overwriting whatever the user has clicked since.
	const applyGenerationRef = useRef(0)

	const isEnabled = isSplitActive && isAutoOpenFloorPlan

	// Put the plan's selection back after the panel-driven re-layout, but ONLY if it was lost.
	// Apryse resets the Primary viewer's tool when a panel opens and the outgoing tool's `switchOut`
	// deselects everything (see core/viewerTools.ts → preservePaneToolModes), which left the design's
	// two-entity selection empty while both sidebars still read correctly — an eref with no handles
	// and an empty batch pill.
	//
	// `getSelectedEntities()` skips counterpart ids, so a non-empty result means the USER's own
	// entity survived and there is nothing to repair. Both selects go through `selectInPaneSilently`:
	// a programmatic select fires the same `annotationSelected` this hook listens to, and without the
	// suppression it would flip the Primary role to whichever pane was restored last.
	const restoreSelection = useCallback(
		(plan: TSmartNavPlan, generation: number) => {
			const restore = () => {
				if (applyGenerationRef.current !== generation) return
				if (AnnotationService.getSelectedEntities().length > 0) return
				selectInPaneSilently(plan.primaryPane, () => {
					if (plan.selection.kind === "eref") AnnotationService.selectEref(plan.selection.eref.id)
					else if (plan.selection.kind === "ecall") AnnotationService.selectEcall(plan.selection.ecall.id)
					else AnnotationService.selectFurniture(plan.selection.furniture.id)
				})
				const counterpart = plan.counterparts.items[plan.index]
				if (!counterpart) return
				selectInPaneSilently(plan.secondaryPane, () =>
					AnnotationService.selectEntityAsCounterpart(plan.counterparts.kind, counterpart.id, plan.primaryPane),
				)
			}
			requestAnimationFrame(restore)
			window.setTimeout(restore, SELECTION_RESTORE_SETTLE_MS)
		},
		[selectInPaneSilently],
	)

	const notifySelection = useCallback(
		(selection: TSmartNavSelection | null, pane: TViewerPane, hasOwnScroll: boolean) => {
			if (isApplyingRef.current) {
				smartNavDebug("gate", { reason: "applying", pane })
				return
			}
			setPrimaryPane(isSplitActive ? pane : "primary")
			// The gate, spelled out. A `notify` with no `plan` after it means the machine declined, and
			// there are two very different reasons it can: the user has not checked "Auto open floor
			// plan", or `isSplitActive` is false in the store while two panes are still on screen (a
			// desync). Both look identical from the outside, so log which one it was.
			smartNavDebug("gate", {
				isEnabled,
				isSplitActive,
				isAutoOpenFloorPlan,
				primaryPane,
				livePaneCount: instanceRef.current?.Core.getDocumentViewers?.().length ?? null,
				hasSelection: selection !== null,
			})
			if (!isEnabled || !selection) return

			const plan = resolveSmartNavigationPlan({
				selection,
				pane,
				allEcalls: allEcallsRef.current,
				findErefById: findErefWithFurnitureById,
				indexMemory: indexMemoryRef.current,
			})

			smartNavDebug("plan", {
				primaryPane: plan.primaryPane,
				secondaryPane: plan.secondaryPane,
				selection: `${plan.selection.kind}:${getSelectionEntityId(plan.selection)}`,
				counterpartKind: plan.counterparts.kind,
				counterpartIds: plan.counterparts.items.map((item) => item.id),
				index: plan.index,
			})

			if (plan.counterparts.items.length === 0) {
				// Nothing is connected on the other side, so there is nothing to auto-display. Any
				// session this hook had started belongs to a previous selection and is now stale.
				if (jumpSessionRef.current?.memoryKey != null) closeJumpSession()
				return
			}

			const options: TJumpSessionOptions = {
				pane: plan.secondaryPane,
				sourcePane: plan.primaryPane,
				memoryKey: plan.memoryKey,
				// The counterpart is selected IN ADDITION to what the user clicked: the design shows both
				// entities selected, and dropping the user's own selection would silently disable the eref
				// batch pill and the Delete/Backspace shortcut, both of which resolve exactly one eref.
				additiveItems: true,
			}
			// Snapshot BEFORE the apply: it is the sidebar this opens that re-lays out the panes, and
			// that re-layout is what moves the Primary pane's viewport and resets its tool mode.
			//
			// SKIPPED when the caller already has its own scroll in flight for this pane (a fresh
			// click, `hasOwnScroll`) — PR #1347 review: the snapshot is taken from the pane's PRE-click
			// position, and frameAnnotations's settle loop needs up to a couple hundred ms to land the
			// click's own target. `preservePaneViewport`'s delayed correction (350ms later) can't tell
			// "unwanted drift from the sidebar's width change" apart from "our own centering finishing
			// exactly on schedule" — it saw the latter as drift and reverted a correctly-centered eref
			// back to its pre-click position every time. frameAnnotations's own pass loop already
			// re-measures panel insets on every pass, so it needs no help staying correct through a
			// width change — this protection is only for a pane that ISN'T actively centering right
			// now (the `annotationSelected` echo re-select, `hasOwnScroll: false`).
			const restorePrimaryViewport = hasOwnScroll
				? () => {}
				: preservePaneViewport(instanceRef.current, plan.primaryPane)
			const restoreToolModes = preservePaneToolModes(instanceRef.current)
			// Drop whatever the PREVIOUS plan was still scrolling in the pane that's about to become
			// Secondary — never the pane the user just clicked (plan.primaryPane), whose own fresh
			// scroll is already running by this point and must be left alone (PR #1347 review: this
			// used to cancel both panes and broke the clicked pane's own centering under Auto open
			// floor plan). See AnnotationService.cancelScrolls's own comment for the full reasoning.
			AnnotationService.cancelScrolls(plan.secondaryPane)
			const generation = ++applyGenerationRef.current
			isApplyingRef.current = true
			try {
				// Three code paths (D7 plus the furniture addition, PR #1347 review): the selected
				// entity's kind decides which counterpart set the Secondary pane steps through, and the
				// panes are already resolved. Furniture has no reverse direction — it is a source only.
				if (plan.selection.kind === "eref" && plan.counterparts.kind === "ecall") {
					startEcallJumpSession(
						{ kind: "eref", eref: plan.selection.eref },
						plan.counterparts.items,
						plan.index,
						options,
					)
				} else if (plan.selection.kind === "furniture" && plan.counterparts.kind === "ecall") {
					startEcallJumpSession(
						{ kind: "furniture", furniture: plan.selection.furniture },
						plan.counterparts.items,
						plan.index,
						options,
					)
				} else if (plan.selection.kind === "ecall" && plan.counterparts.kind === "eref") {
					startErefJumpSession(
						{ kind: "ecall", ecall: plan.selection.ecall },
						plan.counterparts.items,
						plan.index,
						options,
					)
				}
			} finally {
				isApplyingRef.current = false
				restorePrimaryViewport()
				restoreToolModes()
				// The tool reset above already deselected everything on its way out, and putting the
				// tool back does not bring the selection with it — so re-assert what the design says
				// must be selected: the entity the user clicked, plus the counterpart. Deferred past
				// the same re-layout, and skipped entirely unless the selection was actually lost.
				restoreSelection(plan, generation)
			}
		},
		[
			allEcallsRef,
			closeJumpSession,
			findErefWithFurnitureById,
			instanceRef,
			isAutoOpenFloorPlan,
			isEnabled,
			isSplitActive,
			primaryPane,
			jumpSessionRef,
			restoreSelection,
			setPrimaryPane,
			startEcallJumpSession,
			startErefJumpSession,
		],
	)

	notifyRef.current = notifySelection

	// Remember where the user got to in a smart-nav session, so flipping Primary back to that pane
	// resumes it instead of restarting at 1. Only sessions this hook started carry a memory key.
	useEffect(() => {
		const key = jumpSession?.memoryKey
		if (key == null || jumpSession == null) return
		indexMemoryRef.current[key] = jumpSession.index
	}, [jumpSession])

	// Turning the gate off — closing split screen, or unchecking "Auto open floor plan" — takes this
	// hook's own session down with it (AC16 / AC17). Sessions started from a sidebar's "view
	// connected" action have no memory key and are deliberately left alone: they are a single-pane
	// affordance that predates this feature.
	useEffect(() => {
		if (isEnabled) return
		if (jumpSessionRef.current?.memoryKey != null) closeJumpSession()
	}, [isEnabled, closeJumpSession, jumpSessionRef])

	// There is no second pane to own the Primary role in single-viewer mode.
	useEffect(() => {
		if (!isSplitActive) setPrimaryPane("primary")
	}, [isSplitActive, setPrimaryPane])

	// Everything this hook owns is per-editor-session and must not outlive the modal: the remembered
	// positions describe one quotation's entities, and the auto mode is opt-in per session. The viewer
	// instance itself is deliberately kept mounted across open/close (#1119), so this cannot live in an
	// unmount cleanup — that is why the toggle used to stay checked when a different quotation was
	// opened next.
	useEffect(() => {
		if (isModalOpen) return
		indexMemoryRef.current = {}
		setAutoOpenFloorPlan(false)
		setPrimaryPane("primary")
	}, [isModalOpen, setAutoOpenFloorPlan, setPrimaryPane])

	return { primaryPane, isAutoOpenFloorPlan, notifySelection }
}
